import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Bot,
  ChevronDown,
  Clock3,
  FileOutput,
  MessageSquare,
  Minus,
  Paperclip,
  Play,
  PlusCircle,
  Send,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/features/auth/auth-provider";
import { postApiObservabilityTraceQuery } from "@/lib/api/generated/observability/observability";
import {
  postApiSessionsArtifactsGet,
  postApiSessionsBootstrap,
  postApiSessionsFilesGet,
  postApiSessionsFilesUpload,
  postApiSessionsGatewayAccess,
  postApiSessionsGet,
  postApiSessionsOpen,
} from "@/lib/api/generated/sessions/sessions";
import type {
  ObservabilityTraceEventDto,
  SessionArtifactGetRequest,
  SessionArtifactRecordDto,
  SessionBootstrapResponse,
  SessionFileGetRequest,
  SessionFileRecordDto,
  SessionFileUploadRequest,
  SessionFileUploadResponse,
  SessionGetResponse,
  SessionGatewayAccessResponse,
  SessionOpenRequest,
  SessionOpenResponse,
} from "@/lib/api/generated/control-api.schemas";
import {
  runSessionGatewayTurn,
  visibleAssistantText,
} from "../../../../../packages/shared-types/src/index.js";
import { base64ToBlob, fileToBase64 } from "@/lib/file-codec";
import { toastMutationError } from "@/lib/utils/formatting";

const platformTestQueryKeys = {
  bootstrap: (tenantId: string) => ["tenants", tenantId, "platform-test-console", "sessions", "bootstrap"] as const,
  detail: (tenantId: string, sessionId: string) =>
    ["tenants", tenantId, "platform-test-console", "sessions", sessionId] as const,
  observability: (tenantId: string, sessionId: string, agentId: string) =>
    ["tenants", tenantId, "platform-test-console", "observability", sessionId, agentId] as const,
};

type PlatformGatewaySendInput = {
  agentId: string;
  sessionId: string;
  text: string;
};

export function PlatformTestConsole() {
  const { tenant } = useAuth();
  const tenantId = tenant?.tenantId ?? "";
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [composerText, setComposerText] = useState("");
  const [scriptText, setScriptText] = useState("Compare the uploaded insurance offers.");
  const [scriptRunning, setScriptRunning] = useState(false);
  const [testSession, setTestSession] = useState<SessionOpenResponse["session"] | null>(null);
  const [openingForAgentId, setOpeningForAgentId] = useState("");

  const bootstrapQuery = useQuery({
    queryKey: platformTestQueryKeys.bootstrap(tenantId || "none"),
    queryFn: async () => await postApiSessionsBootstrap({}),
    enabled: Boolean(open && tenantId),
    staleTime: 60_000,
  });

  const visibleAgents = bootstrapQuery.data?.visibleAgents ?? [];
  const selectedAgent =
    visibleAgents.find((agent) => agent.agentId === selectedAgentId) ?? visibleAgents[0] ?? null;
  const sessionId = testSession?.sessionId ?? "";

  const openSessionMutation = useMutation({
    mutationFn: async (request: SessionOpenRequest) => await postApiSessionsOpen(request),
    onSuccess: (result) => {
      setTestSession(result.session);
      if (!tenantId) return;
      queryClient.setQueryData<SessionBootstrapResponse | undefined>(
        platformTestQueryKeys.bootstrap(tenantId),
        (current) => upsertPlatformTestSessionSummary(current, result.session),
      );
      queryClient.setQueryData<SessionGetResponse | undefined>(
        platformTestQueryKeys.detail(tenantId, result.session.sessionId),
        () => ({ session: result.session, messages: [], files: [], artifacts: [] }),
      );
    },
    onError: (error) => {
      toast.error(toastMutationError(error, "Failed to open platform test session"));
    },
    onSettled: () => setOpeningForAgentId(""),
  });

  const sessionQuery = useQuery({
    queryKey: platformTestQueryKeys.detail(tenantId || "none", sessionId || "none"),
    queryFn: async () => await postApiSessionsGet({ sessionId }),
    enabled: Boolean(open && tenantId && sessionId),
    refetchOnWindowFocus: false,
  });

  const observabilityQuery = useQuery({
    queryKey: platformTestQueryKeys.observability(
      tenantId || "none",
      sessionId || "none",
      selectedAgent?.agentId ?? "none",
    ),
    queryFn: async () => await postApiObservabilityTraceQuery({
      sessionId,
      agentId: selectedAgent?.agentId ?? null,
      connectorKey: null,
      since: testSession?.createdAt ? new Date(Date.parse(testSession.createdAt) - 120_000).toISOString() : null,
      until: null,
      limit: 120,
    }),
    enabled: Boolean(open && tenantId && sessionId && selectedAgent),
    refetchInterval: open && sessionId ? 2_000 : false,
    refetchOnWindowFocus: false,
  });
  const traceEventCount = observabilityQuery.data?.events?.length ?? 0;

  function refreshGatewaySession(access: SessionGatewayAccessResponse) {
    if (!tenantId) return;
    queryClient.setQueryData<SessionBootstrapResponse | undefined>(
      platformTestQueryKeys.bootstrap(tenantId),
      (current) => upsertPlatformTestSessionSummary(current, access.session),
    );
    void queryClient.invalidateQueries({
      queryKey: platformTestQueryKeys.detail(tenantId, access.sessionId),
    });
    void queryClient.invalidateQueries({
      queryKey: platformTestQueryKeys.bootstrap(tenantId),
    });
  }

  const sendMutation = useMutation({
    mutationFn: async (request: PlatformGatewaySendInput) => {
      const access = await postApiSessionsGatewayAccess({
        agentId: request.agentId,
        sessionId: request.sessionId,
      });
      queryClient.setQueryData<SessionBootstrapResponse | undefined>(
        platformTestQueryKeys.bootstrap(tenantId),
        (current) => upsertPlatformTestSessionSummary(current, access.session),
      );
      await runSessionGatewayTurn({
        access,
        text: request.text,
        client: {
          id: "webchat-ui",
          displayName: "Agentiq Platform Test",
          version: "agentiq-web",
          platform: "web",
          mode: "webchat",
        },
        onRuntimeEvent: () => undefined,
        onSessionChanged: () => refreshGatewaySession(access),
      });
      return access;
    },
    onSuccess: (access) => {
      refreshGatewaySession(access);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (request: SessionFileUploadRequest) =>
      await postApiSessionsFilesUpload(request) as SessionFileUploadResponse,
    onSuccess: (result) => {
      if (!tenantId) return;
      queryClient.setQueryData<SessionBootstrapResponse | undefined>(
        platformTestQueryKeys.bootstrap(tenantId),
        (current) => upsertPlatformTestSessionSummary(current, result.session),
      );
      queryClient.setQueryData<SessionGetResponse | undefined>(
        platformTestQueryKeys.detail(tenantId, result.session.sessionId),
        (current) => upsertPlatformTestSessionFile(current, result.file, result.session),
      );
    },
  });

  useEffect(() => {
    if (!selectedAgentId && visibleAgents[0]) {
      setSelectedAgentId(visibleAgents[0].agentId);
    }
  }, [selectedAgentId, visibleAgents]);

  if (!tenantId) {
    return null;
  }

  async function openCleanTestSession() {
    if (!selectedAgent) {
      toast.error("Select an agent before opening a test session.");
      return null;
    }

    setOpeningForAgentId(selectedAgent.agentId);
    const peerKey = `platform-test:${selectedAgent.agentId}:${crypto.randomUUID()}`;
    const result = await openSessionMutation.mutateAsync({
      agentId: selectedAgent.agentId,
      peerKey,
    });
    return result.session;
  }

  async function ensureTestSession() {
    if (testSession?.agentId === selectedAgent?.agentId) return testSession;
    return await openCleanTestSession();
  }

  async function sendTestMessage(text: string) {
    if (!selectedAgent) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    const session = await ensureTestSession();
    if (!session) return;
    await sendMutation.mutateAsync({
      agentId: selectedAgent.agentId,
      sessionId: session.sessionId,
      text: trimmed,
    });
  }

  async function handleSend() {
    try {
      await sendTestMessage(composerText);
      setComposerText("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send test message.");
    }
  }

  async function handleUpload(files: FileList | null) {
    if (!files?.length || !selectedAgent) return;
    try {
      const session = await ensureTestSession();
      if (!session) return;
      for (const file of Array.from(files)) {
        await uploadMutation.mutateAsync({
          agentId: selectedAgent.agentId,
          sessionId: session.sessionId,
          filename: file.name,
          mimeType: file.type || undefined,
          contentBase64: await fileToBase64(file),
        });
      }
      toast.success(files.length === 1 ? "File uploaded to test chat." : `${files.length} files uploaded to test chat.`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload file.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  async function runScript() {
    const messages = scriptText.split(/\r?\n/u).map((line) => line.trim()).filter(Boolean);
    if (!messages.length) {
      toast.error("Add at least one test message.");
      return;
    }
    setScriptRunning(true);
    try {
      for (const message of messages) {
        await sendTestMessage(message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to run test script.");
    } finally {
      setScriptRunning(false);
    }
  }

  async function handleDownloadFile(fileId: string) {
    if (!sessionId) return;
    try {
      const response = await postApiSessionsFilesGet({ sessionId, fileId } as SessionFileGetRequest);
      downloadBlob(response.file.filename, base64ToBlob(response.contentBase64, response.file.mimeType));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to download file.");
    }
  }

  async function handleDownloadArtifact(artifactId: string) {
    if (!sessionId) return;
    try {
      const response = await postApiSessionsArtifactsGet({ sessionId, artifactId } as SessionArtifactGetRequest);
      downloadBlob(response.artifact.filename, base64ToBlob(response.contentBase64, response.artifact.mimeType));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to download artifact.");
    }
  }

  return (
    <div className="fixed bottom-5 right-5 z-50">
      {open ? (
        <div className="flex h-[min(820px,calc(100vh-3rem))] w-[min(1180px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-[28px] border border-[oklch(0.89_0.006_255)] bg-[oklch(0.985_0.004_255)] shadow-2xl">
          <header className="border-b border-[oklch(0.9_0.006_255)] px-6 py-4">
            <div className="flex min-w-0 items-center justify-between gap-4">
              <div className="min-w-0">
                <h2 className="truncate text-xl font-semibold text-[oklch(0.22_0.014_255)]">Platform test console</h2>
                <p className="mt-1 text-sm text-[oklch(0.48_0.018_255)]">
                  Fresh session, natural messages, real runtime, live observability.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Select value={selectedAgent?.agentId ?? ""} onValueChange={setSelectedAgentId}>
                  <SelectTrigger className="h-9 w-[230px] bg-[oklch(0.998_0.003_255)]">
                    <SelectValue placeholder={bootstrapQuery.isLoading ? "Loading agents..." : "Select agent"} />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleAgents.map((agent) => (
                      <SelectItem key={agent.agentId} value={agent.agentId}>
                        {agent.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 rounded-full bg-[oklch(0.998_0.003_255)]"
                  onClick={() => void openCleanTestSession()}
                  disabled={!selectedAgent || openSessionMutation.isPending}
                >
                  <PlusCircle className="size-4" />
                  New session
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-9 rounded-full"
                  onClick={() => setOpen(false)}
                  title="Minimize test console"
                >
                  <Minus className="size-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-9 rounded-full"
                  onClick={() => {
                    setTestSession(null);
                    setComposerText("");
                    setOpen(false);
                  }}
                  title="Close and clear current test state"
                >
                  <X className="size-4" />
                </Button>
              </div>
            </div>
          </header>

          <Tabs defaultValue="conversation" className="flex min-h-0 flex-1 flex-col">
            <div className="border-b border-[oklch(0.9_0.006_255)] bg-[oklch(0.985_0.004_255)] px-6 py-3">
              <TabsList>
                <TabsTrigger value="conversation">Conversation</TabsTrigger>
                <TabsTrigger value="traces">Traces {traceEventCount ? `(${traceEventCount})` : ""}</TabsTrigger>
                <TabsTrigger value="files">Files & Scripts</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="conversation" className="m-0 min-h-0 flex-1">
              <section className="flex h-full min-h-0 flex-col bg-[oklch(0.965_0.004_255)]">
                <div className="flex min-h-14 items-center justify-between gap-3 border-b border-[oklch(0.9_0.006_255)] bg-[oklch(0.985_0.004_255)] px-5">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-full bg-[oklch(0.88_0.025_255)] text-[oklch(0.42_0.065_255)]">
                      <Bot className="size-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-[oklch(0.24_0.015_255)]">
                        {selectedAgent?.name ?? "No agent selected"}
                      </div>
                      <div className="truncate text-xs text-[oklch(0.52_0.018_255)]">
                        {sessionId || (openingForAgentId ? "Opening clean test session..." : "Click New session or send a first message")}
                      </div>
                    </div>
                  </div>
                  <Badge className="shrink-0 rounded-full bg-[oklch(0.94_0.012_255)] text-[oklch(0.35_0.025_255)] hover:bg-[oklch(0.94_0.012_255)]">
                    real Pi turn
                  </Badge>
                </div>
                <PlatformTestThread messages={sessionQuery.data?.messages ?? []} />
                <div className="border-t border-[oklch(0.9_0.006_255)] bg-[oklch(0.985_0.004_255)] p-4">
                  <div className="mx-auto flex max-w-4xl items-end gap-3 rounded-[26px] border border-[oklch(0.86_0.008_255)] bg-[oklch(0.998_0.003_255)] px-3 py-2 shadow-sm">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-10 shrink-0 rounded-full text-[oklch(0.42_0.065_255)]"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!selectedAgent || uploadMutation.isPending || openSessionMutation.isPending}
                      title="Upload files"
                    >
                      <Paperclip className="size-4" />
                    </Button>
                    <Textarea
                      className="min-h-10 flex-1 resize-none border-0 bg-transparent px-0 py-2 text-[15px] leading-6 shadow-none focus-visible:ring-0"
                      rows={2}
                      placeholder="Type a natural test message..."
                      value={composerText}
                      onChange={(event) => setComposerText(event.target.value)}
                      disabled={!selectedAgent || sendMutation.isPending || openSessionMutation.isPending}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          void handleSend();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="icon"
                      className="size-10 shrink-0 rounded-full bg-[oklch(0.58_0.19_255)] text-white hover:bg-[oklch(0.52_0.19_255)]"
                      onClick={() => void handleSend()}
                      disabled={!selectedAgent || sendMutation.isPending || openSessionMutation.isPending}
                      title="Send message"
                    >
                      <Send className="size-4" />
                    </Button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    multiple
                    onChange={(event) => void handleUpload(event.currentTarget.files)}
                  />
                </div>
              </section>
            </TabsContent>

            <TabsContent value="traces" className="m-0 min-h-0 flex-1 overflow-hidden bg-[oklch(0.99_0.003_255)] p-5">
              <PlatformObservabilityPanel
                configured={observabilityQuery.data?.configured}
                message={observabilityQuery.data?.message ?? null}
                events={observabilityQuery.data?.events ?? []}
                loading={observabilityQuery.isFetching}
                errorMessage={observabilityQuery.error instanceof Error ? observabilityQuery.error.message : null}
                sessionId={sessionId}
                fullHeight
              />
            </TabsContent>

            <TabsContent value="files" className="m-0 min-h-0 flex-1 overflow-auto bg-[oklch(0.99_0.003_255)] p-5">
              <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(340px,420px)]">
                <PlatformTestAssets
                  files={sessionQuery.data?.files ?? []}
                  artifacts={sessionQuery.data?.artifacts ?? []}
                  uploading={uploadMutation.isPending}
                  disabled={!selectedAgent || uploadMutation.isPending || openSessionMutation.isPending}
                  onUploadClick={() => fileInputRef.current?.click()}
                  onDownloadFile={(fileId) => void handleDownloadFile(fileId)}
                  onDownloadArtifact={(artifactId) => void handleDownloadArtifact(artifactId)}
                />
                <div className="rounded-2xl border border-[oklch(0.9_0.006_255)] bg-[oklch(0.998_0.003_255)] p-4">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-[oklch(0.48_0.018_255)]">
                    Natural script
                  </Label>
                  <Textarea
                    className="mt-2 min-h-40 resize-none border-[oklch(0.88_0.008_255)] bg-[oklch(0.985_0.004_255)]"
                    rows={7}
                    value={scriptText}
                    onChange={(event) => setScriptText(event.target.value)}
                    placeholder="One natural chat message per line. No file ids, tool names, or hidden flags."
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-3 w-full rounded-full"
                    onClick={() => void runScript()}
                    disabled={!selectedAgent || scriptRunning}
                  >
                    <Play className="size-4" />
                    Run Messages
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <Button
          type="button"
          className="h-12 rounded-full px-5 shadow-xl"
          onClick={() => setOpen(true)}
        >
          <MessageSquare className="size-4" />
          Test Console
        </Button>
      )}
    </div>
  );
}

function PlatformTestThread(params: {
  messages: SessionGetResponse["messages"];
}) {
  const visibleMessages = params.messages.slice(-18);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
      <div className="mx-auto flex min-h-full max-w-3xl flex-col gap-3">
        {visibleMessages.length === 0 ? (
          <div className="m-auto max-w-sm rounded-[24px] border border-dashed border-[oklch(0.84_0.012_255)] bg-[oklch(0.985_0.004_255)] px-6 py-8 text-center">
            <div className="mx-auto mb-3 flex size-10 items-center justify-center rounded-full bg-[oklch(0.88_0.025_255)] text-[oklch(0.42_0.065_255)]">
              <MessageSquare className="size-4" />
            </div>
            <div className="text-sm font-semibold text-[oklch(0.26_0.014_255)]">
              Start a fresh platform test
            </div>
            <div className="mt-1 text-sm leading-6 text-[oklch(0.52_0.018_255)]">
              Attach files or type naturally. The timeline shows model, tool, connector, task, and delivery telemetry as it arrives.
            </div>
          </div>
        ) : null}
        {visibleMessages.map((message) => (
          <PlatformTestBubble key={message.messageId} message={message} />
        ))}
      </div>
    </div>
  );
}

function PlatformTestBubble({ message }: { message: SessionGetResponse["messages"][number] }) {
  const isUser = message.role === "user";
  const text = readMessageDisplayText(message) || "Non-text response";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[76%] rounded-[24px] px-4 py-2.5 text-[15px] leading-6 shadow-sm",
          "whitespace-pre-wrap break-words",
          isUser
            ? "rounded-br-md bg-[oklch(0.58_0.19_255)] text-white"
            : "rounded-bl-md border border-[oklch(0.88_0.008_255)] bg-[oklch(0.998_0.003_255)] text-[oklch(0.24_0.015_255)]",
        ].join(" ")}
      >
        <div>{text}</div>
        <div className={`mt-1 text-[11px] ${isUser ? "text-white/70" : "text-[oklch(0.55_0.018_255)]"}`}>
          {new Date(message.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

function readMessageDisplayText(message: SessionGetResponse["messages"][number]): string {
  const text = message.text?.trim() || readMessageContentText(message.content);
  if (!text) {
    return "";
  }
  return message.role === "assistant" ? visibleAssistantText(text) : text;
}

function PlatformObservabilityPanel(params: {
  configured?: boolean;
  message: string | null;
  events: ObservabilityTraceEventDto[];
  loading: boolean;
  errorMessage: string | null;
  sessionId: string;
  fullHeight?: boolean;
}) {
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const recentEvents = [...params.events]
    .sort((left, right) => Date.parse(left.timestamp) - Date.parse(right.timestamp))
    .slice(-60);
  const highlightedEvents = params.events.filter((event) => {
    const attributes = readTraceAttributes(event.attributes);
    return Boolean(
      attributes["openclaw.connector.key"] ||
      attributes["openclaw.tool.name"] ||
      attributes["openclaw.model"] ||
      attributes["openclaw.memory.operation"],
    );
  }).length;

  useEffect(() => {
    if (!params.fullHeight || !timelineRef.current) return;
    timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
  }, [params.fullHeight, recentEvents.length]);

  return (
    <section
      className={[
        "rounded-2xl border border-[oklch(0.9_0.006_255)] bg-[oklch(0.998_0.003_255)]",
        params.fullHeight ? "flex h-full min-h-0 flex-col p-4" : "p-3",
      ].join(" ")}
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[oklch(0.48_0.018_255)]">
            <Activity className="size-4" />
            Session timeline
          </div>
          {params.sessionId ? (
            <div className="mt-1 truncate text-xs text-[oklch(0.55_0.018_255)]">
              {params.events.length} events · {highlightedEvents} highlighted
            </div>
          ) : null}
        </div>
        <Badge variant="outline" className="rounded-full text-[11px]">
          {params.loading ? "polling" : "live"}
        </Badge>
      </div>
      {!params.sessionId ? (
        <p className="rounded-xl border border-dashed border-[oklch(0.86_0.008_255)] px-3 py-4 text-sm text-[oklch(0.55_0.018_255)]">
          Start a clean session to correlate telemetry.
        </p>
      ) : params.configured === false ? (
        <p className="rounded-xl border border-[oklch(0.88_0.08_70)] bg-[oklch(0.97_0.025_70)] px-3 py-4 text-sm text-[oklch(0.42_0.07_70)]">
          {params.message ?? "Observability telemetry store is not configured."}
        </p>
      ) : params.errorMessage ? (
        <p className="rounded-xl border border-[oklch(0.84_0.09_25)] bg-[oklch(0.97_0.026_25)] px-3 py-4 text-sm text-[oklch(0.42_0.09_25)]">
          {params.errorMessage}
        </p>
      ) : recentEvents.length === 0 ? (
        <p className="rounded-xl border border-dashed border-[oklch(0.86_0.008_255)] px-3 py-4 text-sm text-[oklch(0.55_0.018_255)]">
          No telemetry matched this session yet.
        </p>
      ) : (
        <div
          ref={timelineRef}
          className={params.fullHeight ? "min-h-0 flex-1 overflow-auto pr-2" : "max-h-[26rem] overflow-auto pr-1"}
        >
          <div className="grid gap-2">
            {recentEvents.map((event, index) => (
              <PlatformObservabilityEvent
                key={`${event.timestamp}:${event.traceId ?? event.spanId ?? event.name}:${index}`}
                event={event}
                previousEvent={index > 0 ? recentEvents[index - 1] : null}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function PlatformObservabilityEvent({
  event,
  previousEvent,
}: {
  event: ObservabilityTraceEventDto;
  previousEvent: ObservabilityTraceEventDto | null;
}) {
  const attributes = readTraceAttributes(event.attributes);
  const statusTone = resolveTraceStatusTone(event);
  const duration = formatTraceDuration(event.durationNano);
  const sincePrevious = formatTraceGap(previousEvent?.timestamp, event.timestamp);
  const summaryRows = buildTraceSummaryRows(event, attributes, sincePrevious);
  const connectorKey = stringifyTraceValue(attributes["openclaw.connector.key"]);
  const operationKey = stringifyTraceValue(attributes["openclaw.operation.key"]);
  const toolName = stringifyTraceValue(attributes["openclaw.tool.name"]);
  const model = stringifyTraceValue(attributes["openclaw.model"]);
  const memoryOperation = stringifyTraceValue(attributes["openclaw.memory.operation"]);
  const isHighlighted = Boolean(connectorKey || operationKey || toolName || model || memoryOperation);
  const showStatusBadge = Boolean(statusTone.label);

  return (
    <details className={[
      "group/event rounded-xl border px-3 py-2.5 transition-colors",
      isHighlighted
        ? "border-[oklch(0.79_0.08_255)] bg-[oklch(0.975_0.018_255)]"
        : "border-[oklch(0.9_0.006_255)] bg-[oklch(0.985_0.004_255)]",
    ].join(" ")}>
      <summary className="grid cursor-pointer list-none grid-cols-[auto_1fr_auto] items-start gap-3">
        <span className={["mt-1 size-2.5 rounded-full", statusTone.dotClass].join(" ")} />
        <div className="min-w-0 space-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-[oklch(0.24_0.015_255)]">{event.name}</span>
            {isHighlighted ? (
              <Badge variant="secondary" className="rounded-full text-[10px]">
                {connectorKey || toolName || operationKey || model || memoryOperation}
              </Badge>
            ) : null}
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[oklch(0.52_0.018_255)]">
            <span>{event.source}</span>
            <span className="inline-flex items-center gap-1">
              <Clock3 className="size-3" />
              {formatTraceTime(event.timestamp)}
            </span>
            {sincePrevious ? <span>{sincePrevious} after previous</span> : null}
            {duration ? <span>{duration}</span> : null}
          </div>
          {summaryRows.length ? (
            <div className="grid gap-1 pt-1 sm:grid-cols-2">
              {summaryRows.slice(0, 8).map((row) => (
                <div key={row.label} className="min-w-0 rounded-lg bg-[oklch(0.998_0.003_255)] px-2 py-1">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-[oklch(0.55_0.018_255)]">{row.label}</div>
                  <div className="truncate text-xs text-[oklch(0.28_0.016_255)]">{row.value}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {showStatusBadge ? (
            <Badge variant={statusTone.badgeVariant} className="rounded-full text-[10px]">
              {statusTone.label}
            </Badge>
          ) : null}
          <ChevronDown className="size-4 text-[oklch(0.48_0.018_255)] transition-transform group-open/event:rotate-180" />
        </div>
      </summary>
      <div className="mt-3 grid gap-2 border-t border-[oklch(0.9_0.006_255)] pt-3">
        <TraceDetailsBlock
          title="Trace ids"
          data={{
            traceId: event.traceId,
            spanId: event.spanId,
            parentSpanId: event.parentSpanId,
            timestamp: event.timestamp,
            source: event.source,
            status: event.status,
            severity: event.severity,
            durationNano: event.durationNano,
          }}
        />
        <TraceDetailsBlock title="Attributes" data={attributes} />
        <TraceDetailsBlock title="Raw payload" data={event.raw} />
      </div>
    </details>
  );
}

function TraceDetailsBlock({ title, data }: { title: string; data: unknown }) {
  return (
    <details className="group/block rounded-lg border border-[oklch(0.9_0.006_255)] bg-[oklch(0.998_0.003_255)]">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[oklch(0.42_0.018_255)]">
        {title}
        <ChevronDown className="size-4 transition-transform group-open/block:rotate-180" />
      </summary>
      <pre className="max-h-64 overflow-auto border-t border-[oklch(0.9_0.006_255)] bg-[oklch(0.94_0.006_255)] p-3 text-[11px] leading-5 text-[oklch(0.28_0.016_255)]">
        {JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );
}

function PlatformTestAssets(params: {
  files: SessionFileRecordDto[];
  artifacts: SessionArtifactRecordDto[];
  uploading: boolean;
  disabled: boolean;
  onUploadClick: () => void;
  onDownloadFile: (fileId: string) => void;
  onDownloadArtifact: (artifactId: string) => void;
}) {
  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-[oklch(0.24_0.015_255)]">Attachments</h3>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-full"
          onClick={params.onUploadClick}
          disabled={params.disabled || params.uploading}
        >
          <Upload className="size-4" />
          {params.uploading ? "Uploading" : "Attach"}
        </Button>
      </div>
      <PlatformAssetGroup
        icon={<Paperclip className="size-4" />}
        title="Session files"
        emptyLabel="No files attached."
        items={params.files.map((file) => ({
          id: file.fileId,
          title: file.filename,
          subtitle: `${file.mimeType} · ${formatBytes(file.sizeBytes)}`,
          onDownload: () => params.onDownloadFile(file.fileId),
        }))}
      />
      <PlatformAssetGroup
        icon={<FileOutput className="size-4" />}
        title="Generated outputs"
        emptyLabel="No outputs yet."
        items={params.artifacts.map((artifact) => ({
          id: artifact.artifactId,
          title: artifact.filename,
          subtitle: `${artifact.mimeType} · ${formatBytes(artifact.sizeBytes)}`,
          onDownload: () => params.onDownloadArtifact(artifact.artifactId),
        }))}
      />
    </div>
  );
}

function PlatformAssetGroup(params: {
  icon: ReactNode;
  title: string;
  emptyLabel: string;
  items: Array<{ id: string; title: string; subtitle: string; onDownload: () => void }>;
}) {
  return (
    <section className="rounded-2xl border border-[oklch(0.9_0.006_255)] bg-[oklch(0.998_0.003_255)] p-3">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[oklch(0.48_0.018_255)]">
        {params.icon}
        {params.title}
      </div>
      {params.items.length ? (
        <div className="grid gap-2">
          {params.items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={item.onDownload}
              className="min-w-0 rounded-xl border border-[oklch(0.9_0.006_255)] bg-[oklch(0.985_0.004_255)] px-3 py-2 text-left transition-colors hover:bg-[oklch(0.96_0.006_255)]"
            >
              <div className="truncate text-sm font-medium text-[oklch(0.24_0.015_255)]">{item.title}</div>
              <div className="truncate text-xs text-[oklch(0.55_0.018_255)]">{item.subtitle}</div>
            </button>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-[oklch(0.86_0.008_255)] px-3 py-5 text-sm text-[oklch(0.55_0.018_255)]">
          {params.emptyLabel}
        </div>
      )}
    </section>
  );
}

function readMessageContentText(content: SessionGetResponse["messages"][number]["content"]): string | undefined {
  const textBlocks = (content ?? [])
    .map((block) => (isJsonRecord(block) && typeof block.text === "string" ? block.text : undefined))
    .filter((text): text is string => Boolean(text?.trim()));
  return textBlocks.join("\n").trim() || undefined;
}

function isJsonRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readTraceAttributes(value: unknown): Record<string, unknown> {
  if (isJsonRecord(value)) return value;
  return {};
}

function buildTraceSummaryRows(
  event: ObservabilityTraceEventDto,
  attributes: Record<string, unknown>,
  sincePrevious: string | undefined,
) {
  const rows: Array<{ label: string; value: string }> = [];
  const add = (label: string, value: unknown) => {
    const text = stringifyTraceValue(value);
    if (text) rows.push({ label, value: formatTraceSummaryValue(text) });
  };

  add("message", attributes["openclaw.content"]);
  add("error", attributes["openclaw.error"] ?? attributes["openclaw.connector.error.message"]);
  add("outcome", attributes["openclaw.outcome"] ?? attributes["openclaw.connector.status"]);
  add("model", attributes["openclaw.model"]);
  add("provider", attributes["openclaw.provider"]);
  add("api", attributes["openclaw.model.api"]);
  add("transport", attributes["openclaw.model.transport"]);
  add("think", attributes["openclaw.think_level"]);
  add("prompt", formatCount(attributes["openclaw.prompt.length"], "chars"));
  add("content", formatCount(attributes["openclaw.content.length"], "chars"));
  add("context budget", formatCount(attributes["openclaw.context.token_budget"], "tokens"));
  add("ttfb", formatDurationMs(attributes["openclaw.response.ttfb_ms"]));
  add("request", formatCount(attributes["openclaw.request.payload_bytes"], "bytes"));
  add("stream", formatCount(attributes["openclaw.response.stream_bytes"], "bytes"));
  add("tool", attributes["openclaw.tool.name"]);
  add("connector", attributes["openclaw.connector.key"]);
  add("capability", attributes["openclaw.capability.key"]);
  add("operation", attributes["openclaw.operation.key"]);
  add("type", attributes["openclaw.operation.type"]);
  add("execution", attributes["openclaw.execution.host"]);
  add("memory", attributes["openclaw.memory.operation"]);
  add("approval", attributes["openclaw.approval.status"]);
  add("delivery", attributes["openclaw.delivery.to"]);
  add("payloads", attributes["openclaw.delivery.payload_count"]);
  add("media", attributes["openclaw.delivery.media_count"]);
  add("since previous", sincePrevious);
  add("duration", formatDurationMs(attributes["openclaw.duration_ms"]) ?? formatTraceDuration(event.durationNano));

  return rows.slice(0, 8);
}

function stringifyTraceValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") return value.trim() || undefined;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function resolveTraceStatusTone(event: ObservabilityTraceEventDto) {
  const label = event.severity ?? event.status ?? "event";
  const normalized = label.toLowerCase();
  if (normalized === "unset") {
    return {
      label: "",
      badgeVariant: "outline" as const,
      dotClass: "bg-[oklch(0.58_0.19_255)]",
    };
  }

  if (normalized.includes("error") || normalized.includes("fail")) {
    return {
      label,
      badgeVariant: "destructive" as const,
      dotClass: "bg-[oklch(0.58_0.19_25)]",
    };
  }

  if (normalized.includes("warn")) {
    return {
      label,
      badgeVariant: "secondary" as const,
      dotClass: "bg-[oklch(0.72_0.14_75)]",
    };
  }

  return {
    label,
    badgeVariant: "outline" as const,
    dotClass: "bg-[oklch(0.58_0.19_255)]",
  };
}

function formatTraceSummaryValue(value: string): string {
  const singleLine = value.replace(/\s+/gu, " ").trim();
  return singleLine.length > 180 ? `${singleLine.slice(0, 177)}...` : singleLine;
}

function formatCount(value: unknown, unit: string): string | undefined {
  const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(numeric) ? `${numeric.toLocaleString()} ${unit}` : undefined;
}

function formatDurationMs(value: unknown): string | undefined {
  const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(numeric) || numeric <= 0) return undefined;
  if (numeric < 1000) return `${numeric.toFixed(1)} ms`;
  return `${(numeric / 1000).toFixed(2)} s`;
}

function formatTraceGap(previousTimestamp: string | undefined, currentTimestamp: string): string | undefined {
  if (!previousTimestamp) return undefined;
  const deltaMs = Date.parse(currentTimestamp) - Date.parse(previousTimestamp);
  if (!Number.isFinite(deltaMs) || deltaMs < 0) return undefined;
  if (deltaMs < 1000) return `${deltaMs} ms`;
  if (deltaMs < 60_000) return `${(deltaMs / 1000).toFixed(deltaMs < 10_000 ? 1 : 0)} s`;
  return `${(deltaMs / 60_000).toFixed(1)} min`;
}

function formatTraceTime(value: string): string {
  return new Date(value).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

function formatTraceDuration(value: number | null | undefined): string | undefined {
  if (!value || value <= 0) return undefined;
  const milliseconds = value / 1_000_000;
  if (milliseconds < 1) return `${(value / 1_000).toFixed(1)} us`;
  if (milliseconds < 1000) return `${milliseconds.toFixed(1)} ms`;
  return `${(milliseconds / 1000).toFixed(2)} s`;
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function sortPlatformTestSessions(sessions: SessionBootstrapResponse["sessions"]) {
  return [...sessions].sort((left, right) => {
    const leftTs = Date.parse(left.lastMessageAt || left.updatedAt || left.createdAt || "");
    const rightTs = Date.parse(right.lastMessageAt || right.updatedAt || right.createdAt || "");
    return rightTs - leftTs;
  });
}

function upsertPlatformTestSessionSummary(
  current: SessionBootstrapResponse | undefined,
  session: SessionBootstrapResponse["sessions"][number],
): SessionBootstrapResponse | undefined {
  if (!current) return current;

  const existing = current.sessions.filter((entry) => entry.sessionId !== session.sessionId);
  return {
    ...current,
    sessions: sortPlatformTestSessions([session, ...existing]),
  };
}

function upsertPlatformTestSessionFile(
  current: SessionGetResponse | undefined,
  file: SessionFileRecordDto,
  session: SessionGetResponse["session"],
): SessionGetResponse {
  const existingFiles = current?.files.filter((entry) => entry.fileId !== file.fileId) ?? [];
  return {
    session,
    messages: current?.messages ?? [],
    files: [...existingFiles, file],
    artifacts: current?.artifacts ?? [],
  };
}

function downloadBlob(filename: string, blob: Blob): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
